import { isSuccess } from '@/context/services/type';
import { isCommonError } from '@/libs/typeguard/isError';
import {
  CommonError,
  UpdateApPayeeInfoRequest,
  useUpdateApPayeeInfo as useBaseUpdateApPayeeInfo,
} from 'ap-openapi';
import { useCallback } from 'react';

type UpdateApPayeeInfoMutateArgs = {
  id: string;
  data: UpdateApPayeeInfoRequest;
};

type UpdateApPayeeInfoSuccess = {
  status: 'success';
};

type UpdateApPayeeInfoCommonError = {
  status: 'error';
  errors: CommonError;
  statusCode: number;
};

type UpdateApPayeeInfoInvalidError = {
  status: 'invalid';
  errors: unknown;
};

export type UpdateApPayeeInfoResult =
  | UpdateApPayeeInfoSuccess
  | UpdateApPayeeInfoCommonError
  | UpdateApPayeeInfoInvalidError;

/**
 * Custom hook for updating ApPayee information
 * Wraps the base updateApPayeeInfo mutation with proper error handling
 * and returns a normalized response structure
 */
export const useUpdateApPayeeInfo = () => {
  const { mutateAsync: _mutateAsync } = useBaseUpdateApPayeeInfo();

  const mutateAsync = useCallback(
    async (
      args: UpdateApPayeeInfoMutateArgs
    ): Promise<UpdateApPayeeInfoResult> => {
      const resp = await _mutateAsync(args);

      if (isSuccess(resp.status)) {
        return {
          status: 'success',
        };
      } else {
        if (isCommonError(resp.data)) {
          return {
            status: 'error',
            errors: resp.data,
            statusCode: resp.status,
          };
        } else {
          return {
            status: 'invalid',
            errors: resp.data ?? {},
          };
        }
      }
    },
    [_mutateAsync]
  );

  return { mutateAsync };
};
